/**
 * Phase 83 — Trial Report Editor
 * ===============================
 *
 * Reads a Trial Report from /api/trial-reports/{id} and exposes:
 *   • Header with athlete, dates, status badge.
 *   • Snapshot block (read-only attendance + match + mental + injury data
 *     pulled by the backend from the canonical modules).
 *   • 14 toggleable sections — each renders either a `RichTextEditor`,
 *     a recommendation dropdown, or a KV-display depending on type.
 *   • Action bar: Save draft / Finalise / Archive / Download PDF.
 *
 * Saves use PATCH /api/trial-reports/{id} so finalised reports cannot
 * be silently mutated — the backend re-sanitises HTML on every PATCH.
 *
 * Permission semantics:
 *   • Admin / Staff → full edit, finalise, archive.
 *   • Player / Parent → read-only view of finalised reports.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import RichTextEditor from '../components/RichTextEditor';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import EvidenceUploader from '../components/EvidenceUploader';
import ReportResourceCards from '../components/ReportResourceCards';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Save, CheckCircle2, Archive, Trash2,
  Calendar, Users, Activity, Brain, Apple, Target,
  Eye, EyeOff, Ruler, Dumbbell, Printer, RefreshCw, AlertCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  draft:     'bg-amber-100 text-amber-700 border-amber-200',
  finalized: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  archived:  'bg-gray-100 text-gray-500 border-gray-200',
};

// Section order MUST match SECTION_KEYS in trial_report_models.py
// Phase 84.7 — each section carries its own "kicker" (chapter tag) so
// the Web Report has visible information hierarchy instead of 14
// identical cards.
const SECTIONS = [
  { key: 'trial_overview',            label: '1. Trial Overview',                kind: 'narrative', kicker: 'Section 01  ·  Overview',              hint: 'Free notes for context. Snapshot pre-filled above.' },
  { key: 'attendance',                label: '2. Attendance',                    kind: 'attendance', kicker: 'Section 02  ·  Performance data',    hint: 'Comment on punctuality, consistency.' },
  { key: 'match_involvement',         label: '3. Match / Friendly Involvement',  kind: 'match',      kicker: 'Section 03  ·  Performance data',    hint: 'Coach observations on match exposure.' },
  { key: 'biometrics',                label: '3b. Biometrics',                   kind: 'biometrics', kicker: 'Section 03b  ·  Performance data',   hint: 'Latest height / weight / BMI / body composition during the trial window.' },
  { key: 'fitness_assessments',       label: '3c. Fitness Assessments',          kind: 'fitness',    kicker: 'Section 03c  ·  Performance data',   hint: 'Sprint, endurance, strength tests recorded in this window.' },
  { key: 'coach_evaluation',          label: '4. Coach Evaluation',              kind: 'narrative',  kicker: 'Section 04  ·  Evaluations',         hint: 'Overall written evaluation.' },
  { key: 'technical_evaluation',      label: '5. Technical Evaluation',          kind: 'narrative',  kicker: 'Section 05  ·  Evaluations',         hint: 'First touch, passing, dribbling, finishing, ball control.' },
  { key: 'tactical_evaluation',       label: '6. Tactical Evaluation',           kind: 'narrative',  kicker: 'Section 06  ·  Evaluations',         hint: 'Positioning, decision making, defensive awareness, game understanding, transitions.' },
  { key: 'physical_evaluation',       label: '7. Physical Evaluation',           kind: 'narrative',  kicker: 'Section 07  ·  Evaluations',         hint: 'Speed, strength, endurance, agility, athleticism.' },
  { key: 'mental_evaluation',         label: '8. Mental Evaluation',             kind: 'narrative',  kicker: 'Section 08  ·  Evaluations',         hint: 'Work ethic, coachability, communication, concentration, confidence, attitude, discipline.' },
  { key: 'nutrition_recommendations', label: '9. Nutrition Recommendations',     kind: 'narrative',  kicker: 'Section 09  ·  Supporting info',     hint: 'Concise recommendations only.' },
  { key: 'key_strengths',             label: '10. Key Strengths',                kind: 'narrative',  kicker: 'Section 10  ·  Development plan',    hint: 'Bullet list or rich text.' },
  { key: 'areas_to_improve',          label: '11. Areas to Improve',             kind: 'narrative',  kicker: 'Section 11  ·  Development plan',    hint: 'Bullet list or rich text.' },
  { key: 'pfap_development_potential',label: '12. PFAP Development Potential',   kind: 'narrative',  kicker: 'Section 12  ·  Development plan',    hint: 'Honest, encouraging, development-focused.' },
  { key: 'recommendation',            label: '13. Recommendation',               kind: 'recommendation', kicker: 'Section 13  ·  Development plan',hint: 'Pick one + optional comments.' },
  { key: 'next_steps',                label: '14. Next Steps',                   kind: 'narrative',  kicker: 'Section 14  ·  Development plan',    hint: 'Examples: Join U17, individual training, return after recovery.' },
];

const StatusBadge = ({ status }) => (
  <Badge className={`text-[10px] uppercase tracking-wide border ${STATUS_TONE[status] || ''}`}>
    {status || 'draft'}
  </Badge>
);

export default function TrialReportEditor() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { hasModuleAccess } = useAuth();
  // Phase 83 — Rely on the global axios Authorization header set by
  // AuthContext (token is not exposed via useAuth()).

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recommendationOptions, setRecommendationOptions] = useState([]);
  const [dirty, setDirty] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [r, opt] = await Promise.all([
        axios.get(`${API}/api/trial-reports/${reportId}`),
        axios.get(`${API}/api/trial-reports/options/recommendation`)
          .catch(() => ({ data: { options: [] } })),
      ]);
      setReport(r.data);
      setRecommendationOptions(opt.data?.options || []);
    } catch (err) {
      if (err.response?.status === 401) return;
      toast.error(err.response?.data?.detail || 'Failed to load Trial Report');
      navigate('/trial-reports');
    } finally {
      setLoading(false);
    }
  }, [reportId, navigate]);

  useEffect(() => { fetch(); }, [fetch]);

  // Mutation helpers ---------------------------------------------------
  const setCoach = (key, value) => {
    setReport(r => ({ ...r, coach_input: { ...(r.coach_input || {}), [key]: value } }));
    setDirty(true);
  };
  const setSectionToggle = (key, v) => {
    setReport(r => ({ ...r, sections: { ...(r.sections || {}), [key]: v } }));
    setDirty(true);
  };
  const setRecommendation = (patch) => {
    setReport(r => ({ ...r, recommendation: { ...(r.recommendation || {}), ...patch } }));
    setDirty(true);
  };
  const setEvidence = (sectionKey, items) => {
    setReport(r => ({
      ...r,
      evidence: { ...(r.evidence || {}), [sectionKey]: items },
    }));
    setDirty(true);
  };

  // Persistence --------------------------------------------------------
  const save = async (opts = {}) => {
    setSaving(true);
    try {
      const body = {
        coach_input: report.coach_input,
        sections: report.sections,
        recommendation: report.recommendation,
        evidence: report.evidence || {},
        data_source_modes: report.data_source_modes || {},
        title: report.title,
        position: report.position,
        squad: report.squad,
        ...(opts.status ? { status: opts.status } : {}),
      };
      const res = await axios.patch(`${API}/api/trial-reports/${reportId}`, body);
      setReport(res.data);
      setDirty(false);
      toast.success(opts.status ? `Trial Report ${opts.status}.` : 'Draft saved.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const _openPrintVersion = async () => {
    // Fetch through axios so the JWT Authorization header is sent, then
    // open the resulting HTML in a new tab via a Blob URL. Works
    // regardless of cookie-vs-header auth strategy.
    try {
      const res = await axios.get(`${API}/api/trial-reports/${reportId}/print`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        toast.error('Please allow pop-ups to open the print version.');
        // Revoke immediately so we don't leak — user got a toast.
        URL.revokeObjectURL(url);
        return;
      }
      // Revoke after a delay so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Could not open the print version.');
    }
  };

  const _downloadHtml = async () => {
    try {
      const res = await axios.get(`${API}/api/trial-reports/${reportId}/html`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TrialReport_${(report?.athlete_name || 'athlete').replace(/\s+/g, '_')}_${report?.trial_start_date}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('HTML report downloaded');
    } catch {
      toast.error('HTML export failed. Try Print Version instead.');
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/api/trial-reports/${reportId}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TrialReport_${(report?.athlete_name || 'athlete').replace(/\s+/g, '_')}_${report?.trial_start_date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Guaranteed fallback: even if PDF generation blew up, staff must
      // never be left with only a dead-end error. Auto-open the
      // print-ready HTML version and surface the reference_id for ops.
      let referenceId = null;
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === 'function') {
          const txt = await blob.text();
          const j = JSON.parse(txt);
          referenceId = j?.detail?.reference_id || null;
        }
      } catch { /* ignore */ }
      toast.warning(
        referenceId
          ? `PDF generation encountered an issue (Ref: ${referenceId}). Your report is safe. Opening the print-ready version now.`
          : 'PDF generation encountered an issue. Your report is safe. Opening the print-ready version now.',
        { duration: 8000 }
      );
      await _openPrintVersion();
    }
  };

  // Phase 84.5 — Refresh Athlete Data
  const [refreshing, setRefreshing] = useState(false);
  const refreshSnapshot = async () => {
    if (!window.confirm(
      "Refresh auto-generated sections from the latest athlete data?\n\n"
      + "Your written narratives, uploaded evidence, section toggles and "
      + "the recommendation will NOT be changed."
    )) return;
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/api/trial-reports/${reportId}/refresh`);
      const fresh = res.data?.report;
      const diff = res.data?.diff || {};
      if (fresh) {
        setReport(fresh);
        setDirty(false);
      }
      const changed = (diff.changed_keys || []).length;
      toast.success(
        changed
          ? `Snapshot refreshed — ${changed} aggregated section(s) updated.`
          : "Snapshot refreshed — no source-data changes detected.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  const deleteReport = async () => {
    if (!window.confirm('Delete this Trial Report? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/trial-reports/${reportId}`);
      toast.success('Trial Report deleted.');
      navigate('/trial-reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed.');
    }
  };

  if (loading) {
    return <Layout><p className="p-6 text-sm text-gray-500" data-testid="trial-editor-loading">Loading…</p></Layout>;
  }
  if (!report) return null;

  const sections = report.sections || {};
  // Phase 87 — action controls are driven by module permissions, not the raw
  // Staff role. Admin passes every check automatically via hasModuleAccess.
  const canEdit = hasModuleAccess(PM.TRIAL_REPORTS, 'edit') && report.status !== 'archived';
  const canDelete = hasModuleAccess(PM.TRIAL_REPORTS, 'delete');
  const canExport = hasModuleAccess(PM.TRIAL_REPORTS_EXPORT, 'view');
  const snap = report.snapshot || {};

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6 max-w-5xl mx-auto pb-24" data-testid="trial-report-editor">
        {/* Sticky action bar — Phase 83.3 web-report-first workflow */}
        <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-2 bg-white/85 dark:bg-gray-900/85 backdrop-blur border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-wrap gap-2"
             data-testid="trial-editor-sticky-bar">
          <Button variant="ghost" onClick={() => navigate('/trial-reports')} data-testid="trial-editor-back">
            <ArrowLeft size={14} className="mr-1.5" /> Back to Trial Reports
          </Button>
          <div className="flex items-center gap-2">
            {canEdit && report.status === 'draft' && (
              <Button
                variant="outline"
                size="sm"
                onClick={refreshSnapshot}
                disabled={refreshing}
                className={report.refresh_available ? 'border-amber-400 text-amber-700 hover:bg-amber-50' : ''}
                data-testid="trial-editor-refresh"
                title="Re-aggregate athlete data. Written narratives, evidence and section toggles are preserved."
              >
                <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing…' : 'Refresh Data'}
              </Button>
            )}
            <StatusBadge status={report.status} />
            {!canEdit && canExport && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.print()}
                className="hidden md:inline-flex"
                data-testid="trial-editor-print"
              >
                <Printer size={14} className="mr-1.5" /> Print
              </Button>
            )}
            {canExport && (
            <div className="inline-flex rounded-md shadow-sm" data-testid="trial-editor-export-group">
              <Button variant="outline" size="sm" onClick={downloadPdf} className="rounded-r-none" data-testid="trial-editor-pdf">
                <Download size={14} className="mr-1.5" /> Download PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={_openPrintVersion}
                className="rounded-none border-l-0 hidden sm:inline-flex"
                title="Open a print-ready HTML version"
                data-testid="trial-editor-print-version"
              >
                <Printer size={14} className="mr-1.5" /> Print Version
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={_downloadHtml}
                className="rounded-l-none border-l-0 hidden sm:inline-flex"
                title="Download a standalone HTML file"
                data-testid="trial-editor-html"
              >
                <Download size={14} className="mr-1.5" /> HTML
              </Button>
            </div>
            )}
            {canEdit && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => save()}
                  disabled={saving || !dirty}
                  data-testid="trial-editor-save"
                >
                  <Save size={14} className="mr-1.5" /> Save draft
                </Button>
                {report.status === 'draft' && (
                  <Button
                    size="sm"
                    className="bg-pfap-green hover:opacity-90 text-white"
                    onClick={() => save({ status: 'finalized' })}
                    disabled={saving}
                    data-testid="trial-editor-finalize"
                  >
                    <CheckCircle2 size={14} className="mr-1.5" /> Finalise
                  </Button>
                )}
                {report.status !== 'archived' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => save({ status: 'archived' })}
                    disabled={saving}
                    data-testid="trial-editor-archive"
                  >
                    <Archive size={14} className="mr-1.5" /> Archive
                  </Button>
                )}
              </>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="outline"
                onClick={deleteReport}
                className="text-red-600 hover:bg-red-50"
                data-testid="trial-editor-delete"
              >
                <Trash2 size={14} className="mr-1.5" /> Delete
              </Button>
            )}
          </div>
        </div>

        {/* Phase 84.7 — Hero identity block */}
        <HeroCard
          report={report}
          canEdit={canEdit}
          onTitleChange={(t) => { setReport(r => ({ ...r, title: t })); setDirty(true); }}
        />

        {/* Phase 84.5 — "New athlete data available" banner (drafts only) */}
        {canEdit && report.status === 'draft' && report.refresh_available && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-5 py-4 flex items-start gap-3"
            data-testid="refresh-banner"
          >
            <AlertCircle size={16} className="text-amber-700 dark:text-amber-300 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900 dark:text-amber-100">
                New athlete data is available
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
                Biometrics, fitness assessments, attendance or profile information changed since this draft was last refreshed.
                Written narratives, evidence and section toggles will be preserved.
              </p>
              {report.source_refreshed_at && (
                <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
                  Last refreshed: {new Date(report.source_refreshed_at).toLocaleString()}
                  {report.source_refreshed_by_name ? ` by ${report.source_refreshed_by_name}` : ''}
                </p>
              )}
            </div>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={refreshSnapshot}
              disabled={refreshing}
              data-testid="refresh-banner-btn"
            >
              <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Now
            </Button>
          </div>
        )}

        {/* Phase 84.7 — Executive Summary (surfaces recommendation + key stats at the top) */}
        <ExecutiveSummaryCard
          recommendation={report.recommendation}
          snapshot={snap}
          hidden={sections.recommendation === false && sections.snapshot === false}
        />

        {/* Sections */}
        {SECTIONS.map(s => (
          <SectionBlock
            key={s.key}
            section={s}
            enabled={sections[s.key] !== false}
            onToggle={(v) => setSectionToggle(s.key, v)}
            canEdit={canEdit}
            value={s.kind === 'recommendation' ? report.recommendation : report.coach_input?.[s.key]}
            onChange={(v) => s.kind === 'recommendation'
              ? setRecommendation(v)
              : setCoach(s.key, v)}
            recommendationOptions={recommendationOptions}
            snapshot={snap}
            evidenceItems={report.evidence?.[s.key] || []}
            onEvidenceChange={(items) => setEvidence(s.key, items)}
            dataSourceMode={(report.data_source_modes || {})[s.key] || 'auto'}
            onDataSourceModeChange={(v) => {
              setReport(r => ({
                ...r,
                data_source_modes: { ...(r.data_source_modes || {}), [s.key]: v },
              }));
              setDirty(true);
            }}
          />
        ))}

        {/* Phase 83.3 — Auto-collected supporting resources card grid */}
        <ReportResourceCards
          htmlSources={[
            ...SECTIONS.filter(s => s.kind === 'narrative' || s.kind === 'biometrics' || s.kind === 'fitness')
              .map(s => report.coach_input?.[s.key]),
            report.recommendation?.comments,
          ]}
        />
      </div>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// Phase 84.7 — Hero identity block (replaces the plain title card).
const HeroCard = ({ report, canEdit, onTitleChange }) => {
  const trialWindow = `${report.trial_start_date || ''} → ${report.trial_end_date || ''}`;
  return (
    <div
      className="rounded-xl bg-gradient-to-br from-pfap-green to-[#1e2916] text-white px-6 py-8 shadow-sm"
      data-testid="trial-hero"
    >
      <p className="text-[10px] uppercase tracking-[0.15em] text-pfap-sage/90 font-semibold">
        Pro-Football Academy Portugal  ·  Trial Report
      </p>
      <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          {canEdit ? (
            <Input
              value={report.title}
              onChange={(e) => onTitleChange(e.target.value)}
              className="text-3xl md:text-4xl font-bold border-none px-0 bg-transparent text-white placeholder-white/60 focus-visible:ring-0 h-auto"
              data-testid="trial-editor-title"
            />
          ) : (
            <h1 className="text-3xl md:text-4xl font-bold leading-tight" data-testid="trial-editor-title">
              {report.title}
            </h1>
          )}
          <p className="mt-2 text-sm text-white/80" data-testid="trial-editor-meta">
            <Users size={13} className="inline mr-1.5 opacity-80" />
            <span className="font-semibold">{report.athlete_name}</span>
            <span className="mx-2 opacity-40">·</span>
            {report.squad || report.athlete_squad || '—'}
            <span className="mx-2 opacity-40">·</span>
            {report.position || '—'}
          </p>
          <p className="mt-1 text-xs text-white/60" data-testid="trial-editor-window">
            <Calendar size={11} className="inline mr-1 opacity-70" />
            Trial period: <span className="font-medium text-white/85">{trialWindow}</span>
            {report.created_by_name && (
              <>
                <span className="mx-2 opacity-40">·</span>
                Coach: <span className="font-medium text-white/85">{report.created_by_name}</span>
              </>
            )}
          </p>
        </div>
        <StatusBadge status={report.status} />
      </div>
    </div>
  );
};

// Phase 84.7 — Executive Summary sits directly after the hero.
// Surfaces the RECOMMENDATION front-and-centre, then attendance / match
// output as key stats. Auto-hides fields with no data.
const ExecutiveSummaryCard = ({ recommendation, snapshot, hidden }) => {
  if (hidden) return null;
  const rec = recommendation || {};
  const att = snapshot?.attendance || {};
  const matches = snapshot?.matches || {};
  const hasReco = !!(rec.choice && String(rec.choice).trim());
  const attRate = att.rate;
  const hasAtt = attRate !== null && attRate !== undefined;
  const hasMatch = !!(matches.count || matches.minutes);
  const hasOutput = !!(matches.goals || matches.assists);
  if (!hasReco && !hasAtt && !hasMatch && !hasOutput) return null;

  const tone = recommendationTone(rec.choice);
  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="executive-summary"
    >
      {hasReco && (
        <div className="flex items-stretch">
          <div className={`w-1.5 ${tone.accentBg}`} aria-hidden />
          <div className="flex-1 px-6 py-5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-pfap-green font-semibold">
              Recommendation
            </p>
            <p className="mt-1 text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="exec-recommendation">
              {rec.choice}
            </p>
            {rec.comments && stripHtml(rec.comments).length > 0 && (
              <p className="mt-1.5 text-xs text-gray-500 line-clamp-2">
                {stripHtml(rec.comments).slice(0, 200)}
                {stripHtml(rec.comments).length > 200 ? '…' : ''}
              </p>
            )}
          </div>
        </div>
      )}
      {(hasAtt || hasMatch || hasOutput) && (() => {
        const cols = [hasAtt, hasMatch, hasOutput].filter(Boolean).length;
        const gridColsClass = cols === 1 ? 'grid-cols-1'
          : cols === 2 ? 'grid-cols-1 sm:grid-cols-2'
          : 'grid-cols-1 sm:grid-cols-3';
        return (
          <div className={`grid ${gridColsClass} divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-gray-800 ${hasReco ? 'border-t border-gray-100 dark:border-gray-800' : ''}`}>
            {hasAtt && (
              <div className="px-6 py-4" data-testid="exec-attendance">
                <p className="text-[10px] uppercase tracking-[0.15em] text-pfap-green font-semibold">Attendance</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{attRate}%</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {att.sessions_attended || 0} of {att.sessions_assigned || att.sessions_total || 0} sessions
                </p>
              </div>
            )}
            {hasMatch && (
              <div className="px-6 py-4" data-testid="exec-match">
                <p className="text-[10px] uppercase tracking-[0.15em] text-pfap-green font-semibold">Match time</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{matches.minutes || 0}′</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {matches.count || 0} match{matches.count === 1 ? '' : 'es'}
                </p>
              </div>
            )}
            {hasOutput && (
              <div className="px-6 py-4" data-testid="exec-output">
                <p className="text-[10px] uppercase tracking-[0.15em] text-pfap-green font-semibold">Output</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {matches.goals || 0}
                  <span className="text-gray-300 mx-2">·</span>
                  {matches.assists || 0}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">Goals · Assists</p>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

const SnapTile = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50 dark:bg-gray-800/40" data-testid={testid}>
    <Icon size={16} className="text-pfap-green shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{value}</p>
      <p className="text-[10px] text-gray-400 truncate">{hint}</p>
    </div>
  </div>
);

// Section block — handles narrative + recommendation kinds.
// Phase 84.7 — refreshed look: kicker (chapter tag) in brand green +
// larger title + hairline divider inside the card, and a soft neutral
// background for "data" kinds so they read differently from prose.
const SectionBlock = ({
  section, enabled, onToggle, canEdit, value, onChange,
  recommendationOptions, snapshot, evidenceItems, onEvidenceChange,
  dataSourceMode, onDataSourceModeChange,
}) => {
  const isNarrative = section.kind === 'narrative';
  const isReco      = section.kind === 'recommendation';
  const isBio       = section.kind === 'biometrics';
  const isFit       = section.kind === 'fitness';
  const isAtt       = section.kind === 'attendance';
  const isMatch     = section.kind === 'match';
  const isData      = isBio || isFit || isAtt || isMatch;
  // Phase 86.2 — surface a "Data source" dropdown for Biometrics /
  // Fitness only when the choice is actually meaningful: either the
  // athlete has records in multiple places (so switching mode changes
  // the display) OR the report is currently rendering a fallback (so
  // the coach can override / hide it). Keep the UI clean in the common
  // "only one possible source" case.
  const sel = isBio
    ? snapshot?.biometrics_selection
    : isFit ? snapshot?.fitness_selection : null;
  const showSourcePicker = canEdit && (isBio || isFit) && enabled && sel && (
    // Both a trial-window record AND a wider-range record exist → give
    // staff the choice.
    (sel.has_trial_window_record && sel.has_any_record)
    // OR the report currently shows the fallback → coach may want to
    // switch back to strict / hide.
    || sel.selection_mode === 'latest_available'
    // OR the coach already picked a non-auto mode → let them see + edit
    // their own selection.
    || (dataSourceMode && dataSourceMode !== 'auto')
  );
  return (
    <section
      className={`rounded-xl border overflow-hidden ${
        isData
          ? 'border-gray-200 dark:border-gray-800 bg-gray-50/40 dark:bg-gray-900/40'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
      }`}
      data-testid={`section-${section.key}`}
    >
      <header className="px-6 pt-5 pb-3 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {section.kicker && (
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-pfap-green">
              {section.kicker}
            </p>
          )}
          <h2 className="mt-0.5 text-lg font-bold text-gray-900 dark:text-gray-100" data-testid={`section-${section.key}-title`}>
            {section.label}
          </h2>
          {canEdit && section.hint && (
            <p className="text-[11px] text-gray-400 mt-1">{section.hint}</p>
          )}
        </div>
        {canEdit ? (
          <div className="flex items-center gap-3 pt-1">
            {showSourcePicker && (
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">
                <span>Data source</span>
                <select
                  value={dataSourceMode || 'auto'}
                  onChange={(e) => onDataSourceModeChange && onDataSourceModeChange(e.target.value)}
                  className="text-[11px] rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1.5 py-0.5 normal-case tracking-normal"
                  data-testid={`section-${section.key}-source-mode`}
                >
                  <option value="auto">Auto</option>
                  <option value="trial_window">Trial-period assessment</option>
                  <option value="latest_available">Latest available assessment</option>
                </select>
              </label>
            )}
            <span className="text-[10px] uppercase tracking-wide text-gray-400">
              {enabled ? 'Included' : 'Hidden'}
            </span>
            <Switch
              checked={enabled}
              onCheckedChange={onToggle}
              data-testid={`section-${section.key}-toggle`}
            />
          </div>
        ) : (
          !enabled && (
            <span className="text-[10px] text-gray-400 flex items-center gap-1">
              <EyeOff size={12} /> Hidden
            </span>
          )
        )}
      </header>
      {enabled && (() => {
        const hasNarrative= isNarrative && !!(value && stripHtml(value).length > 0);
        const hasReco     = isReco && (
          !!(value?.choice && String(value.choice).trim())
          || !!(value?.comments && stripHtml(value.comments).length > 0)
        );
        const hasBio      = isBio && (
          !!snapshot?.latest_biometrics || !!snapshot?.biometrics_baseline
          || !!(value && stripHtml(value).length > 0)
        );
        const hasFit      = isFit && (
          !!snapshot?.fitness_session || !!snapshot?.fitness_baseline
          || (Array.isArray(snapshot?.fitness_results) && snapshot.fitness_results.length > 0)
          || (Array.isArray(snapshot?.fitness_results_normalized) && snapshot.fitness_results_normalized.length > 0)
          || !!(value && stripHtml(value).length > 0)
        );
        const hasAttData  = isAtt && (
          snapshot?.attendance?.rate !== null && snapshot?.attendance?.rate !== undefined
        );
        const hasMatchData = isMatch && !!(snapshot?.matches?.count || snapshot?.matches?.minutes);
        const hasEvidence = Array.isArray(evidenceItems) && evidenceItems.length > 0;
        const showContent = canEdit
          || hasNarrative || hasReco || hasBio || hasFit
          || hasAttData || hasMatchData || hasEvidence;
        if (!showContent) return null;
        return (
          <div className="px-6 pb-6 space-y-4">
            {isNarrative && (
              canEdit ? (
                <RichTextEditor
                  value={value || ''}
                  onChange={onChange}
                  testid={`section-${section.key}-editor`}
                  placeholder="Coach notes…"
                />
              ) : hasNarrative ? (
                <NarrativeRender html={value} testid={`section-${section.key}-readonly`} />
              ) : null
            )}
            {isReco && (
              <RecommendationEditor
                value={value || {}}
                onChange={onChange}
                canEdit={canEdit}
                options={recommendationOptions}
              />
            )}
            {isAtt && (
              <AttendanceBlock
                snapshot={snapshot}
                value={value || ''}
                onChange={onChange}
                canEdit={canEdit}
                windowLabel={`Trial window`}
              />
            )}
            {isMatch && (
              <MatchBlock
                snapshot={snapshot}
                value={value || ''}
                onChange={onChange}
                canEdit={canEdit}
              />
            )}
            {isBio && (
              <BiometricsBlock
                snapshot={snapshot}
                value={value || ''}
                onChange={onChange}
                canEdit={canEdit}
              />
            )}
            {isFit && (
              <FitnessBlock
                snapshot={snapshot}
                value={value || ''}
                onChange={onChange}
                canEdit={canEdit}
              />
            )}
            <EvidenceUploader
              items={evidenceItems}
              onChange={onEvidenceChange}
              canEdit={canEdit}
              testidPrefix={`evidence-${section.key}`}
            />
          </div>
        );
      })()}
    </section>
  );
};

// Phase 84.7 — Shared narrative renderer: preserves rich-text formatting
// (headings, paragraphs, lists, links, emphasis) with generous prose
// spacing so a coach's paragraphs don't collapse into a wall.
const NarrativeRender = ({ html, testid }) => (
  <div
    className="prose prose-slate max-w-none dark:prose-invert
               prose-headings:font-bold prose-headings:text-gray-900
               dark:prose-headings:text-gray-100
               prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2
               prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2
               prose-p:text-[15px] prose-p:leading-7 prose-p:my-3
               prose-ul:my-3 prose-ol:my-3 prose-li:my-1 prose-li:leading-7
               prose-a:text-pfap-green prose-a:font-medium prose-a:no-underline hover:prose-a:underline
               dark:prose-a:text-pfap-sage
               prose-strong:text-gray-900 dark:prose-strong:text-gray-100
               prose-blockquote:border-l-pfap-green prose-blockquote:text-gray-600
               dark:prose-blockquote:text-gray-300"
    data-testid={testid}
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

// Phase 84.7 — New Attendance & Match blocks: give data sections their
// own identity (large stat + progress bar for attendance, tile row for
// match) instead of reusing the plain narrative RichTextEditor path.
const AttendanceBlock = ({ snapshot, value, onChange, canEdit }) => {
  const att = snapshot?.attendance || {};
  const rate = att.rate;
  const assigned = att.sessions_assigned || att.sessions_total || 0;
  const attended = att.sessions_attended || 0;
  const absent = att.sessions_absent || 0;
  const hasNarrative = !!(value && stripHtml(value).length > 0);
  const hasRate = rate !== null && rate !== undefined;
  if (!canEdit && !hasRate && !hasNarrative) return null;
  return (
    <div className="space-y-4" data-testid="attendance-block">
      {hasRate && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-baseline gap-3">
            <p className="text-4xl font-bold text-gray-900 dark:text-gray-100" data-testid="attendance-rate">
              {rate}%
            </p>
            <p className="text-sm text-gray-500">attendance rate</p>
          </div>
          <div className="mt-3 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div
              className="h-full bg-pfap-green transition-all"
              style={{ width: `${Math.max(0, Math.min(100, rate))}%` }}
              data-testid="attendance-bar"
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {attended} of {assigned} sessions attended
            {absent ? <span> · {absent} absent</span> : null}
          </p>
        </div>
      )}
      {canEdit ? (
        <RichTextEditor
          value={value}
          onChange={onChange}
          testid="section-attendance-editor"
          placeholder="Coach notes on punctuality and consistency (optional)…"
        />
      ) : hasNarrative ? (
        <NarrativeRender html={value} testid="section-attendance-readonly" />
      ) : null}
    </div>
  );
};

const MatchBlock = ({ snapshot, value, onChange, canEdit }) => {
  const m = snapshot?.matches || {};
  const hasNarrative = !!(value && stripHtml(value).length > 0);
  const hasData = !!(m.count || m.minutes || m.goals || m.assists);
  if (!canEdit && !hasData && !hasNarrative) return null;
  return (
    <div className="space-y-4" data-testid="match-block">
      {hasData && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile label="Matches"  value={m.count || null}   testid="match-tile-count" />
          <StatTile label="Minutes"  value={m.minutes || null} testid="match-tile-minutes" />
          <StatTile label="Goals"    value={m.goals || null}   testid="match-tile-goals" />
          <StatTile label="Assists"  value={m.assists || null} testid="match-tile-assists" />
        </div>
      )}
      {canEdit ? (
        <RichTextEditor
          value={value}
          onChange={onChange}
          testid="section-match_involvement-editor"
          placeholder="Coach observations on match exposure (optional)…"
        />
      ) : hasNarrative ? (
        <NarrativeRender html={value} testid="section-match_involvement-readonly" />
      ) : null}
    </div>
  );
};

// Phase 83.2 helper — strip HTML for content-emptiness checks in the UI.
const stripHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = String(html);
  return (div.textContent || '').trim();
};

// ── Biometrics + Fitness read-only data blocks ─────────────────────
const KV = ({ label, value, testid }) => (
  <div className="text-xs" data-testid={testid}>
    <span className="text-gray-500">{label}: </span>
    <span className="font-medium text-gray-800 dark:text-gray-100">{value ?? '—'}</span>
  </div>
);

// Phase 84.6 — Premium biometrics visual hierarchy.
// ------------------------------------------------------------------
// The Web Report renders the same grouped shape as the PDF (Body Profile,
// Composition, Vitals, Circumferences, Derived) with a summary-tile row
// on top. Empty fields drop out, empty groups drop out, and the whole
// block collapses if the athlete truly has no data.
const BIO_FIELD_GROUPS = [
  {
    label: 'Body Profile',
    testid: 'bio-group-profile',
    fields: [
      ['height_cm',    'Height',       'cm'],
      ['weight_kg',    'Weight',       'kg'],
      ['body_mass_kg', 'Body mass',    'kg'],
      ['bmi',          'BMI',          null],
    ],
  },
  {
    label: 'Composition',
    testid: 'bio-group-composition',
    fields: [
      ['fat_mass_pct',         'Body fat',         '%'],
      ['muscle_mass_pct',      'Muscle mass',      '%'],
      ['total_body_water_pct', 'Total body water', '%'],
      ['bone_mass_kg',         'Bone mass',        'kg'],
      ['visceral_fat',         'Visceral fat',     null],
    ],
  },
  {
    label: 'Vitals',
    testid: 'bio-group-vitals',
    fields: [
      ['blood_pressure_systolic',  'BP systolic',  'mmHg'],
      ['blood_pressure_diastolic', 'BP diastolic', 'mmHg'],
      ['resting_heart_rate',       'Resting HR',   'bpm'],
      ['blood_o2_saturation',      'SpO2',         '%'],
    ],
  },
  {
    label: 'Circumferences',
    testid: 'bio-group-circumferences',
    fields: [
      ['shoulder_circumference_cm', 'Shoulder',        'cm'],
      ['upper_arm_left_cm',         'Upper arm (L)',   'cm'],
      ['upper_arm_right_cm',        'Upper arm (R)',   'cm'],
      ['forearm_left_cm',           'Forearm (L)',     'cm'],
      ['forearm_right_cm',          'Forearm (R)',     'cm'],
      ['thigh_left_cm',             'Thigh (L)',       'cm'],
      ['thigh_right_cm',            'Thigh (R)',       'cm'],
      ['calf_left_cm',              'Calf (L)',        'cm'],
      ['calf_right_cm',             'Calf (R)',        'cm'],
      ['waist_circumference_cm',    'Waist',           'cm'],
      ['hip_circumference_cm',      'Hip',             'cm'],
      ['thigh_circumference_cm',    'Thigh (legacy)',  'cm'],
      ['arm_circumference_cm',      'Arm (legacy)',    'cm'],
      ['calf_circumference_cm',     'Calf (legacy)',   'cm'],
    ],
  },
  {
    label: 'Derived',
    testid: 'bio-group-derived',
    fields: [
      ['waist_to_hip_ratio',    'Waist / Hip',     null],
      ['waist_to_height_ratio', 'Waist / Height',  null],
      ['sum_of_skinfolds_mm',   'Σ Skinfolds',     'mm'],
    ],
  },
];

const StatTile = ({ label, value, testid }) => (
  <div
    className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-lg font-semibold text-pfap-green">{value ?? '—'}</p>
  </div>
);

const MetricPill = ({ label, value, unit, testid }) => (
  <div
    className="rounded-md border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 min-w-0"
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-wide text-gray-400 truncate">{label}</p>
    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
      {value}
      {unit ? <span className="text-gray-400 font-normal ml-0.5">{unit}</span> : null}
    </p>
  </div>
);

const BiometricsBlock = ({ snapshot, value, onChange, canEdit }) => {
  const latest = snapshot?.latest_biometrics || snapshot?.biometrics_baseline;
  const isBaseline = !snapshot?.latest_biometrics && !!snapshot?.biometrics_baseline;
  const hasNarrative = !!(value && stripHtml(value).length > 0);
  // Phase 83.2 — Suppress the whole block when there is genuinely nothing
  // to show in read-only mode.
  if (!canEdit && !latest && !hasNarrative) return null;

  const renderGroup = (group) => {
    const filled = group.fields
      .map(([key, label, unit]) => [key, label, unit, latest?.[key]])
      .filter(([, , , val]) => val !== null && val !== undefined && val !== '');
    if (!filled.length) return null;
    return (
      <div className="space-y-1.5" key={group.label} data-testid={group.testid}>
        <p className="text-[10px] uppercase tracking-wide font-semibold text-pfap-green">
          {group.label}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {filled.map(([key, label, unit, v]) => (
            <MetricPill
              key={key}
              label={label}
              value={v}
              unit={unit}
              testid={`biometrics-${key}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="biometrics-block">
      {latest && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 space-y-5">
          {/* Top tile row — the "at-a-glance" summary. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="bio-summary-tiles">
            <StatTile
              label="Height"
              value={latest.height_cm ? `${latest.height_cm} cm` : null}
              testid="bio-tile-height"
            />
            <StatTile
              label="Weight"
              value={latest.weight_kg ? `${latest.weight_kg} kg` : null}
              testid="bio-tile-weight"
            />
            <StatTile
              label="BMI"
              value={latest.bmi != null ? latest.bmi : null}
              testid="bio-tile-bmi"
            />
            <StatTile
              label="Body fat"
              value={latest.fat_mass_pct != null ? `${latest.fat_mass_pct}%` : null}
              testid="bio-tile-fat"
            />
          </div>
          <div className="flex items-center gap-2 text-[11px] text-gray-500">
            <Ruler size={12} />
            {snapshot?.biometrics_selection?.selection_mode === 'latest_available' ? (
              <span className="text-gray-700 dark:text-gray-200 font-medium">
                Latest available record
              </span>
            ) : isBaseline ? (
              <span className="text-amber-700 dark:text-amber-300 font-medium">
                Baseline (pre-trial reference)
              </span>
            ) : (
              <span>Latest during trial window</span>
            )}
            {latest.date && <span className="text-gray-400">· {latest.date}</span>}
            {snapshot?.biometrics_selection?.outside_trial_window && (
              <span className="text-gray-500 italic">· Recorded outside the trial period</span>
            )}
          </div>
          {BIO_FIELD_GROUPS.map(renderGroup)}
          {Array.isArray(snapshot?.biometrics_trend) && snapshot.biometrics_trend.length > 1 && (
            <p className="text-[10px] text-gray-400" data-testid="biometrics-trend-count">
              {snapshot.biometrics_trend.length} measurement(s) during the trial window
            </p>
          )}
        </div>
      )}
      {canEdit ? (
        <RichTextEditor
          value={value}
          onChange={onChange}
          testid="section-biometrics-editor"
          placeholder="Coach notes on biometric trends (optional)…"
        />
      ) : hasNarrative ? (
        <NarrativeRender html={value} testid="section-biometrics-readonly" />
      ) : null}
    </div>
  );
};

// Phase 84.8 — Change-tone → tailwind classes for the "Change" column.
const CHANGE_TONE = {
  good: 'text-pfap-green font-semibold',
  bad:  'text-rose-600 font-semibold',
  flat: 'text-gray-400',
};

// Phase 84.8 — De-duplicate the standard/rating strings.
// The DB "threshold_summary" often looks like "Needs Improvement: > 3.70 s".
// We render Rating separately and strip that prefix from the reference
// text so we never print "Needs Improvement" twice on the same row.
const referenceText = (row) => {
  const raw = String(row?.standard || '').trim();
  if (!raw) return '';
  const rating = String(row?.grade || '').trim();
  if (rating && raw.toLowerCase().startsWith(rating.toLowerCase())) {
    const after = raw.slice(rating.length).replace(/^[\s:;,]+/, '').trim();
    if (after) return after;
  }
  return raw;
};

const FitnessGroupTable = ({ category, rows }) => {
  if (!rows || rows.length === 0) return null;
  const showChange = rows.some((r) => (r.change_text || '').trim().length > 0);
  const showRating = rows.some(
    (r) => (r.grade || '').trim().length > 0 || referenceText(r).length > 0,
  );
  // Responsive column widths as percentages of the container.
  const cols = ['test', 'result'];
  if (showRating) cols.push('rating');
  if (showChange) cols.push('change');
  const widthMap = {
    2: { test: '58%', result: '42%' },
    3: { test: '42%', result: '28%', rating: '30%' },
    4: { test: '34%', result: '22%', rating: '26%', change: '18%' },
  }[cols.length];
  return (
    <div className="space-y-1.5" data-testid={`fitness-group-${(category || 'other').toLowerCase().replace(/\s+/g,'-')}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold text-pfap-green">
        {category || 'Other'}
      </p>
      <div className="overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            {cols.map((c) => <col key={c} style={{ width: widthMap[c] }} />)}
          </colgroup>
          <thead className="bg-pfap-green text-white">
            <tr>
              <th className="py-1.5 px-2 text-left font-semibold">Test</th>
              <th className="py-1.5 px-2 text-left font-semibold">Result</th>
              {showRating && <th className="py-1.5 px-2 text-left font-semibold">Rating</th>}
              {showChange && <th className="py-1.5 px-2 text-left font-semibold">Change</th>}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900">
            {rows.map((r, i) => {
              const resultStr =
                r.result === null || r.result === undefined || r.result === ''
                  ? '—'
                  : `${r.result}${r.unit ? ` ${r.unit}` : ''}`;
              const ref = referenceText(r);
              return (
                <tr
                  key={r.id || `${r.test}-${i}`}
                  className="border-t border-gray-100 dark:border-gray-800 align-top"
                  data-testid={`fitness-row-${(r.test || 'na').toLowerCase().replace(/\s+/g,'-')}`}
                >
                  <td className="py-2 px-2 text-gray-800 dark:text-gray-100 break-words">{r.test || '—'}</td>
                  <td className="py-2 px-2 text-gray-800 dark:text-gray-100 break-words">
                    <span className="font-semibold">{resultStr}</span>
                    {ref && (
                      <span className="block text-[10px] text-gray-500 mt-0.5">
                        Reference: {ref}
                      </span>
                    )}
                  </td>
                  {showRating && (
                    <td
                      className="py-2 px-2 font-medium break-words"
                      style={r.grade_color ? { color: r.grade_color } : {}}
                    >
                      {r.grade || '—'}
                    </td>
                  )}
                  {showChange && (
                    <td className={`py-2 px-2 break-words ${CHANGE_TONE[r.change_tone] || CHANGE_TONE.flat}`}>
                      {r.change_text ? (
                        <span className="inline-flex items-center gap-1">
                          {r.change_tone === 'good' && <span aria-hidden>▲</span>}
                          {r.change_tone === 'bad'  && <span aria-hidden>▼</span>}
                          {r.change_tone === 'flat' && <span aria-hidden>■</span>}
                          {r.change_text}
                        </span>
                      ) : '—'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FitnessBlock = ({ snapshot, value, onChange, canEdit }) => {
  const session = snapshot?.fitness_session || snapshot?.fitness_baseline;
  const isBaseline = !snapshot?.fitness_session && !!snapshot?.fitness_baseline;
  // Phase 84.6 — Prefer the adapter-emitted normalized/grouped shape.
  // The backend GET route injects it into legacy finalised snapshots so
  // this branch handles both current and historical reports.
  const grouped = Array.isArray(snapshot?.fitness_results_grouped)
    ? snapshot.fitness_results_grouped : [];
  const normalized = Array.isArray(snapshot?.fitness_results_normalized)
    ? snapshot.fitness_results_normalized : [];
  const hasResults = grouped.length > 0 || normalized.length > 0;
  const hasNarrative = !!(value && stripHtml(value).length > 0);
  if (!canEdit && !session && !hasResults && !hasNarrative) return null;
  return (
    <div className="space-y-4" data-testid="fitness-block">
      {(session || hasResults) && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 space-y-5">
          {/* Session tiles — Phase 84.8: dropped "Category focus" tile
              because (a) it wrapped ugly with 4-6 categories, (b) the
              grouped tables below already show every category as its own
              subsection. Categories now render as small chips below. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="fitness-summary-tiles">
            <StatTile
              label="Session"
              value={session?.session_date || null}
              testid="fitness-tile-date"
            />
            <StatTile
              label="Tests recorded"
              value={normalized.length || null}
              testid="fitness-tile-count"
            />
            <StatTile
              label="Sport"
              value={session?.sport || null}
              testid="fitness-tile-sport"
            />
          </div>
          {grouped.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="fitness-category-chips">
              <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mr-1">Categories</span>
              {grouped.map((g) => (
                <span
                  key={g.category}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-pfap-sage/40 text-pfap-green"
                >
                  {g.category}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 text-[11px] text-gray-500">
            <Dumbbell size={12} />
            {snapshot?.fitness_selection?.selection_mode === 'latest_available' ? (
              <span className="text-gray-700 dark:text-gray-200 font-medium">
                Latest available assessment
              </span>
            ) : isBaseline ? (
              <span className="text-amber-700 dark:text-amber-300 font-medium">
                Baseline (pre-trial reference)
              </span>
            ) : (
              <span>Latest during trial window</span>
            )}
            {session?.session_date && (
              <span className="text-gray-400">· Assessment date: {session.session_date}</span>
            )}
            {session?.evaluator_name && (
              <span className="text-gray-400">· Assessed by {session.evaluator_name}</span>
            )}
            {snapshot?.fitness_selection?.outside_trial_window && (
              <span className="text-gray-500 italic">· Recorded outside the trial period</span>
            )}
          </div>
          {/* Grouped result tables. */}
          {grouped.length > 0
            ? grouped.map((g) => (
                <FitnessGroupTable
                  key={g.category || 'other'}
                  category={g.category}
                  rows={g.results || []}
                />
              ))
            : normalized.length > 0 && (
                <FitnessGroupTable category="Results" rows={normalized} />
              )}
        </div>
      )}
      {canEdit ? (
        <RichTextEditor
          value={value}
          onChange={onChange}
          testid="section-fitness_assessments-editor"
          placeholder="Coach notes on fitness/athletic profile (optional)…"
        />
      ) : hasNarrative ? (
        <NarrativeRender html={value} testid="section-fitness_assessments-readonly" />
      ) : null}
    </div>
  );
};

// Recommendation choices → coloured badge. Keep the palette aligned with
// the PDF (services/report_pdf_branding.recommendation_callout).
// Phase 84.7 — also emits `accentBg` for the ExecutiveSummaryCard's left
// coloured stripe so the hero recommendation shares its tone.
const recommendationTone = (choice) => {
  const lc = String(choice || '').toLowerCase();
  if (!lc) return { dot: '•', badge: 'bg-gray-100 text-gray-600 border-gray-200', accentBg: 'bg-gray-300' };
  if (lc.includes('invite') || lc.includes('join pfap') || lc.includes('recommend for'))
    return { dot: '🟢', badge: 'bg-emerald-50 text-emerald-800 border-emerald-200', accentBg: 'bg-pfap-green' };
  if (lc.includes('not recommended') || lc.includes('reject'))
    return { dot: '🔴', badge: 'bg-rose-50 text-rose-800 border-rose-200', accentBg: 'bg-rose-500' };
  if (lc.includes('continue') || lc.includes('monitor') || lc.includes('extended') || lc.includes('reassess'))
    return { dot: '🟡', badge: 'bg-amber-50 text-amber-800 border-amber-200', accentBg: 'bg-amber-500' };
  return { dot: '•', badge: 'bg-gray-100 text-gray-700 border-gray-200', accentBg: 'bg-gray-300' };
};

const RecommendationEditor = ({ value, onChange, canEdit, options }) => {
  const hasComments = !!(value.comments && stripHtml(value.comments).length > 0);
  const tone = recommendationTone(value.choice);
  return (
    <div className="space-y-3" data-testid="recommendation-block">
      {canEdit ? (
        <select
          value={value.choice || ''}
          onChange={(e) => onChange({ choice: e.target.value })}
          className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
          data-testid="recommendation-select"
        >
          <option value="">— Pick a recommendation —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : value.choice ? (
        <div
          className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold ${tone.badge}`}
          data-testid="recommendation-choice-readonly"
        >
          <span className="text-base">{tone.dot}</span>
          {value.choice}
        </div>
      ) : null}
      {canEdit ? (
        <RichTextEditor
          value={value.comments || ''}
          onChange={(html) => onChange({ comments: html })}
          testid="recommendation-comments-editor"
          placeholder="Optional comments…"
        />
      ) : hasComments ? (
        <NarrativeRender html={value.comments} testid="recommendation-comments-readonly" />
      ) : null}
    </div>
  );
};
